import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

const fmtHours = (h) => Number(Number(h || 0).toFixed(2));
// Rows of a day where the employee logged more than this many hours in total are shaded red.
const OVER_HOURS = 8;

function dateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
}

export default function Status({ userDetails }) {
  const email = userDetails.EmpEmail;
  const [reportees, setReportees] = useState([]);
  // "all" shows every reportee's pending entries, with an Employee column.
  const [selectedEmp, setSelectedEmp] = useState("all");
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Employees who still have entries waiting for review; reloaded after each accept / reject,
  // so someone with nothing left to review drops out of the dropdown.
  const loadReportees = useCallback(() => {
    if (!email) {
      setLoading(false);
      return;
    }
    api
      .getReportees(email)
      .then((list) => {
        setReportees(list);
        setSelectedEmp((cur) => (cur === "all" || list.some((r) => r.empid === cur) ? cur : "all"));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [email]);

  useEffect(() => { loadReportees(); }, [loadReportees]);

  const loadEntries = useCallback(() => {
    setChecked(new Set());
    if (!email || reportees.length === 0) { setEntries([]); return; }
    setLoadingEntries(true);
    api
      .getPendingEntries(email, selectedEmp)
      .then(setEntries)
      .catch((e) => setError(e.message))
      .finally(() => setLoadingEntries(false));
  }, [email, selectedEmp, reportees.length]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const showEmployee = selectedEmp === "all";
  const columns = showEmployee ? 6 : 5;
  const allChecked = entries.length > 0 && checked.size === entries.length;

  const toggle = (id) => {
    const next = new Set(checked);
    next.has(id) ? next.delete(id) : next.add(id);
    setChecked(next);
  };

  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(entries.map((e) => e.id)));

  const review = async (action) => {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      const res = await api.reviewTimeEntries(email, selectedEmp, [...checked], action);
      setNotice(res.message);
      loadReportees();
      loadEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="timesheet-container">
      <div className="timesheet-head">
        <h2>Team Status</h2>
      </div>

      <label className="timesheet-field timesheet-status-employee">
        <span>Employee</span>
        <select
          value={selectedEmp}
          onChange={(e) => { setSelectedEmp(e.target.value); setNotice(""); setError(""); }}
          disabled={loading || reportees.length === 0}
        >
          <option value="all">
            {loading ? "Loading…" : reportees.length === 0 ? "No records available" : "All"}
          </option>
          {reportees.map((r) => (
            <option key={r.empid} value={r.empid}>
              {r.empname} ({r.empid})
            </option>
          ))}
        </select>
      </label>

      {reportees.length > 0 && (
        <div className="timesheet-review">
          <div className="timesheet-review-bar">
            <span className="timesheet-review-count">
              {checked.size > 0 ? `${checked.size} of ${entries.length} selected` : `${entries.length} pending entries`}
            </span>
            <button
              className="timesheet-accept"
              disabled={busy || checked.size === 0}
              onClick={() => review("accept")}
            >
              Accept
            </button>
            <button
              className="timesheet-reject"
              disabled={busy || checked.size === 0}
              onClick={() => review("reject")}
            >
              Reject
            </button>
          </div>

          <div className="timesheet-table-wrap">
            <table className="timesheet-table">
              <thead>
                <tr>
                  <th className="timesheet-col-check">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      disabled={entries.length === 0}
                      onChange={toggleAll}
                      aria-label="Select all entries"
                    />
                  </th>
                  {showEmployee && <th>Employee</th>}
                  <th>Date</th>
                  <th>Project</th>
                  <th>Description</th>
                  <th className="timesheet-num">Hours</th>
                </tr>
              </thead>
              <tbody>
                {loadingEntries ? (
                  <tr><td colSpan={columns} className="timesheet-table-empty">Loading…</td></tr>
                ) : entries.length === 0 ? (
                  <tr><td colSpan={columns} className="timesheet-table-empty">No entries waiting for review.</td></tr>
                ) : (
                  entries.map((x) => (
                    <tr
                      key={x.id}
                      className={`${x.dayhours > OVER_HOURS ? "timesheet-row-over" : ""} ${checked.has(x.id) ? "timesheet-row-checked" : ""}`}
                      title={x.dayhours > OVER_HOURS ? `${fmtHours(x.dayhours)} h logged on this day` : undefined}
                      onClick={() => toggle(x.id)}
                    >
                      <td className="timesheet-col-check">
                        <input
                          type="checkbox"
                          checked={checked.has(x.id)}
                          onChange={() => toggle(x.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select entry from ${x.dateofentry}`}
                        />
                      </td>
                      {showEmployee && (
                        <td className="timesheet-nowrap">
                          <span className="timesheet-project">
                            {x.empname && x.empname !== x.empid ? `${x.empname} (${x.empid})` : x.empid}
                          </span>
                        </td>
                      )}
                      <td className="timesheet-nowrap">{dateLabel(x.dateofentry)}</td>
                      <td><span className="timesheet-project">{x.projectname}</span></td>
                      <td className="timesheet-description">{x.description}</td>
                      <td className="timesheet-num"><span className="timesheet-hours-pill">{fmtHours(x.hoursworked)} h</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {notice && <div className="timesheet-notice">{notice}</div>}
      {error && <div className="timesheet-error">{error}</div>}
    </section>
  );
}
